import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE, type PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { apiGet, mockResponse } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";

/** Same shapes risk-pricing-engine's own lib/api/calibration.ts defines --
 *  both read the same source tables, see this app's
 *  app/repository/loss_calibration.py. */
export interface SegmentCredibility {
  segmentId: string;
  label: string;
  level: "industry" | "office";
  matchedContracts: number;
  ownRate: number;
  tier: string;
  peers: string[];
  peerPooledRate: number;
  z: number;
  blendedRate: number;
}

export interface KGovernanceEntry {
  k: number;
  approvedBy: string;
  approvedAt: string;
  note: string;
}

export interface KGovernance {
  liveK: number;
  estimatedFrom: { segments: number; asOf: string };
  tierBoundaries: number[];
  log: KGovernanceEntry[];
}

/** Blend own experience with the tier pool: Z = n / (n + k). */
export function credibilityWeight(matchedContracts: number, k: number): number {
  if (k <= 0) return 1;
  return matchedContracts / (matchedContracts + k);
}

export function blendRate(ownRate: number, peerPooledRate: number, z: number): number {
  return Number((ownRate * z + peerPooledRate * (1 - z)).toFixed(2));
}

/** Recompute a row against a candidate k without touching the server. */
export function recalibrate(row: SegmentCredibility, k: number): SegmentCredibility {
  const z = credibilityWeight(row.matchedContracts, k);
  return {
    ...row,
    z: Number(z.toFixed(4)),
    blendedRate: blendRate(row.ownRate, row.peerPooledRate, z),
  };
}

export const calibrationKeys = {
  segments: () => ["loss-calibration", "segments"] as const,
  kGovernance: () => ["loss-calibration", "k-governance"] as const,
};

export type SegmentsPage = PagedResult<SegmentCredibility> & { offset: number };

/** One server-side page of Segment Credibility. `k` recomputes Z/blended rate on
 * the server (sandbox / promoted k); `segment` snaps to the page containing that
 * row (the ?segment= deep link) and the response's `offset` says which page that is. */
export function fetchCalibrationSegments(
  opts: { offset: number; k?: number; segment?: string },
  ticket?: TicketSize,
) {
  return apiGet<SegmentsPage>("/loss-calibration/segments", {
    ticket,
    limit: String(PAGE_SIZE),
    offset: String(opts.offset),
    k: opts.k !== undefined ? String(opts.k) : undefined,
    segment: opts.segment,
  });
}

export function fetchKGovernance() {
  return apiGet<KGovernance>("/loss-calibration/k-governance");
}

export function useCalibrationSegments(opts: { offset: number; k?: number; segment?: string }) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: [...calibrationKeys.segments(), opts.offset, opts.k ?? null, opts.segment ?? null, ticket ?? "all"],
    queryFn: () => fetchCalibrationSegments(opts, ticket),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useKGovernance() {
  return useQuery({
    queryKey: calibrationKeys.kGovernance(),
    queryFn: fetchKGovernance,
    staleTime: 60_000,
  });
}

/** Client-side-only simulation, same as risk-pricing-engine's own
 *  usePromoteK -- there is no owned k_governance table on either product
 *  (see app/repository/loss_calibration.py), so "promoting" never reaches a
 *  backend endpoint and resets on reload. */
export function usePromoteK() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ k, note }: { k: number; note: string }) => {
      const entry: KGovernanceEntry = {
        k,
        approvedBy: "You · Pricing Governance",
        approvedAt: new Date().toISOString(),
        note,
      };
      return mockResponse(entry, 200);
    },
    onSuccess: (entry) => {
      queryClient.setQueryData<KGovernance>(calibrationKeys.kGovernance(), (previous) =>
        previous
          ? {
              ...previous,
              liveK: entry.k,
              estimatedFrom: { ...previous.estimatedFrom, asOf: entry.approvedAt.slice(0, 10) },
              log: [entry, ...previous.log],
            }
          : previous,
      );
    },
  });
}

/* ------------------------------------------------------------------ *
 * Loss development (chain ladder)
 * ------------------------------------------------------------------ */

export interface LossDevelopmentPoint {
  vintage: number;
  ageMonths: number;
  chartLossPct: number | null;
  isProjected: boolean;
  projectedLossPct: number | null;
}

export interface DevelopmentFactor {
  fromAge: number;
  toAge: number;
  factor: number;
}

export interface LossDevelopmentLedgerRow {
  vintage: number;
  industry: string;
  ageAtProjectionMonths: number;
  projectedUltimateLossPct: number;
  actualUltimateLossPct: number | null;
  errorPct: number | null;
}

export const DEVELOPMENT_AGES = [12, 24, 36, 48, 60] as const;
export const ULTIMATE_AGE = 60;

export const ALL_INDUSTRIES_LABEL = "All Industry";

export interface LossDevelopmentResponse {
  industry: string | null;
  points: LossDevelopmentPoint[];
  factors: DevelopmentFactor[];
}

/** `basis` is the Realized / Projected / Active toggle: it picks the rows of the tables behind the chart and the ledger
 *  (realized_tf / cohort_active_tf). */
function buildQuery(industries: string[] | undefined, basis: ThreeWayBasis, ticket?: TicketSize): string {
  const params = new URLSearchParams();
  for (const industry of industries ?? []) {
    if (industry && industry !== ALL_INDUSTRIES_LABEL) params.append("industry", industry);
  }
  params.set("basis", basis);
  if (ticket) params.set("ticket", ticket);
  return `?${params.toString()}`;
}

export function fetchLossDevelopment(industries: string[] | undefined, basis: ThreeWayBasis, ticket?: TicketSize) {
  return apiGet<LossDevelopmentResponse>(`/loss-calibration/loss-development${buildQuery(industries, basis, ticket)}`);
}

export function fetchDevelopmentLedger(industries: string[] | undefined, basis: ThreeWayBasis, ticket?: TicketSize) {
  return apiGet<LossDevelopmentLedgerRow[]>(
    `/loss-calibration/development-ledger${buildQuery(industries, basis, ticket)}`,
  );
}

export function useLossDevelopment(industries: string[] | undefined, basis: ThreeWayBasis) {
  const key = industries?.length ? [...industries].sort() : ALL_INDUSTRIES_LABEL;
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-calibration", "loss-development", key, basis, ticket ?? "all"] as const,
    queryFn: () => fetchLossDevelopment(industries, basis, ticket),
    staleTime: 60_000,
  });
}

export function useDevelopmentLedger(industries: string[] | undefined, basis: ThreeWayBasis) {
  const key = industries?.length ? [...industries].sort() : ALL_INDUSTRIES_LABEL;
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-calibration", "development-ledger", key, basis, ticket ?? "all"] as const,
    queryFn: () => fetchDevelopmentLedger(industries, basis, ticket),
    staleTime: 60_000,
  });
}
